import { type QueryClient, useQueries } from '@tanstack/react-query';
import { GetTemplateQueryKey, sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';

const getUniqueTemplateIdsFromTableApplications = (
  tableApplications: TemplateTableApplicationsOutputListV1 | undefined,
): string[] => [
  ...new Set(
    (tableApplications?.applications ?? [])
      .map((app) => app.templateContainerId ?? app.templateGuid)
      .filter((id): id is string => !!id),
  ),
];

/**
 * Invalidates the cached GET /templates/{id} response for a single table template.
 * Call when a template is applied to or retired from a table.
 */
export function invalidateTableTemplate(queryClient: QueryClient, templateId: string) {
  return queryClient.invalidateQueries({ queryKey: GetTemplateQueryKey({ templateId }) });
}

/**
 * Loads full template items for the Edit Template select via React Query.
 * Uses {@link GetTemplateQueryKey} so results share the appserver getTemplate cache.
 * When the set of template ids changes, useQueries fetches/removes queries automatically.
 */
export const useTableTemplates = (
  tableApplications: TemplateTableApplicationsOutputListV1 | undefined,
): { templatesByTemplateId: Record<string, TemplateOutputV1 | undefined>; isLoading: boolean } => {
  const templateIds = getUniqueTemplateIdsFromTableApplications(tableApplications);

  return useQueries({
    queries: templateIds.map((templateId) => ({
      queryKey: GetTemplateQueryKey({ templateId }),
      queryFn: () => sqTemplatesApi.getTemplate({ templateId }).then((response) => response.data),
    })),
    combine: (results) => {
      const templatesByTemplateId: Record<string, TemplateOutputV1 | undefined> = {};
      for (let index = 0; index < templateIds.length; index++) {
        templatesByTemplateId[templateIds[index]] = results[index]?.data;
      }

      return {
        templatesByTemplateId,
        isLoading:
          templateIds.length > 0 &&
          results.some((query) => query.isPending || (query.isFetching && query.data === undefined)),
      };
    },
  });
};
